import React, { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ProductsService, type Product, type ProductoInvima, type Lote } from '../../services/products/products.service';
import { SuppliersService, type Proveedor } from '../../services/suppliers/suppliers.service';
import { usePermissions } from '../../hooks/usePermissions';
import { getProductImageUrl } from '../../utils/product-images';
import { NotificationToast } from '../ui/NotificationToast';
import { AddLoteDrawer } from './AddLoteDrawer';
import toast from 'react-hot-toast';
import {
  Package, Tag, AlertCircle, CheckCircle, X, Loader2,
  ImageIcon, Plus, Trash2, Search, Calendar,
  Boxes, Layers, Pill
} from 'lucide-react';

interface CustomSelectProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ options, value, onChange, placeholder, className }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find(o => o.value === value);

  useEffect(() => {
    const handle = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div ref={ref} className={`relative ${className || ''}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-2.5 rounded-lg border border-gray-300 transition-colors outline-none focus:border-gray-500 cursor-pointer bg-white text-gray-900 hover:border-gray-400"
      >
        <span className={selected ? '' : 'text-gray-400'}>{selected ? selected.label : placeholder || 'Seleccionar'}</span>
      </button>
      {open && (
        <div className="absolute z-50 w-full mt-1 rounded-lg shadow-xl border max-h-60 overflow-y-auto bg-white border-gray-200">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                opt.value === value
                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-gray-700 hover:bg-emerald-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const numericField = (min: number, message: string) => z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    const n = Number(val);
    return isNaN(n) ? undefined : n;
  },
  z.number().min(min, message)
);

const presentacionSchema = z.object({
  id: z.string().optional(),
  nombre: z.string().min(1, 'Nombre requerido'),
  factor: numericField(1, 'Factor mínimo 1'),
  precio: numericField(0, 'Precio no puede ser negativo'),
  precioCompra: z.preprocess(
    (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
    z.number().min(0).optional()
  ),
  esPrincipal: z.boolean().default(false),
});

const productSchema = z.object({
  nombre: z.string().min(1, 'El nombre es requerido').min(2, 'Mínimo 2 caracteres'),
  descripcion: z.string().optional(),
  categoria: z.enum(['medicamentos', 'cuidado-personal', 'higiene-femenina', 'salud-sexual', 'vitaminas-y-suplementos', 'bebes', 'equipos-medicos']),
  receta: z.boolean(),
  codigoBarras: z.string().optional(),
  variante: z.string().optional(),
  registroInvima: z.string().optional(),
  laboratorio: z.string().optional(),
  principioActivo: z.string().optional(),
  ubicacion: z.string().optional(),
  stockBase: numericField(0, 'Stock no puede ser negativo'),
  numeroLote: z.string().optional(),
  fechaVencimiento: z.string().optional(),
  proveedorId: z.string().optional(),
  numeroFactura: z.string().optional(),
  presentacionIdLote: z.string().optional(),
  presentaciones: z.array(presentacionSchema).min(1, 'Debe tener al menos una presentación'),
});



interface CreateProductDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  productToEdit?: Product | null;
  isMinimized?: boolean;
  onMinimize?: () => void;
  onMaximize?: () => void;
}

export const CreateProductDrawer: React.FC<CreateProductDrawerProps> = ({
  isOpen, onClose, onSuccess, productToEdit, isMinimized, onMinimize, onMaximize
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{ title: string; message: string; type: 'success' | 'error' | 'warning' | 'info'; } | null>(null);
  const isEditMode = !!productToEdit;
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<{ id: string; url: string }[]>([]);
  const [invimaQuery, setInvimaQuery] = useState('');
  const [invimaResults, setInvimaResults] = useState<ProductoInvima[]>([]);
  const [invimaLoading, setInvimaLoading] = useState(false);
  const [showInvimaResults, setShowInvimaResults] = useState(false);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [lotesLoading, setLotesLoading] = useState(false);
  const invimaInputRef = useRef<HTMLInputElement>(null);
  const [savingLoteId, setSavingLoteId] = useState<string | null>(null);
  const [isAddLoteOpen, setIsAddLoteOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<Proveedor[]>([]);
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
  const { hasPermission } = usePermissions();

  const { register, handleSubmit, watch, control, formState: { errors, isDirty }, reset, setValue } = useForm<any>({
    resolver: zodResolver(productSchema) as any,
    defaultValues: {
      receta: false,
      stockBase: undefined,
      numeroLote: '',
      fechaVencimiento: '',
      proveedorId: '',
      numeroFactura: '',
      presentacionIdLote: '',
      presentaciones: [{ nombre: 'Unidad', factor: undefined, precio: undefined, precioCompra: undefined, esPrincipal: true }],
    }
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'presentaciones' });

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      SuppliersService.getAllForSelect().then(res => {
        if (res.success) setSuppliers(res.data);
      });
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    // Cleanup object URLs on unmount or when previews change
    return () => {
      imagePreviews.forEach(url => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); });
    };
  }, [imagePreviews]);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      setTimeout(() => {
        invimaInputRef.current?.focus();
      }, 350);
    }
  }, [isOpen, isEditMode]);

  // Inicialización única del drawer cuando se abre (edición o creación)
  useEffect(() => {
    if (!isOpen) return;

    // Limpiar notificaciones y errores
    setShowNotification(false);
    setNotificationConfig(null);
    setErrorMessage(null);

    if (productToEdit) {
      // Modo edición: cargar datos del producto
      reset({
        nombre: productToEdit.nombre,
        descripcion: productToEdit.descripcion || '',
        categoria: productToEdit.categoria as any,
        receta: !!productToEdit.requiereReceta,
        codigoBarras: productToEdit.codigoBarras || '',
        variante: productToEdit.variante || '',
        registroInvima: productToEdit.registroInvima || '',
        laboratorio: productToEdit.laboratorio || '',
        principioActivo: productToEdit.principioActivo || '',
        ubicacion: productToEdit.ubicacion || '',
        stockBase: productToEdit.stockBase ?? undefined,
        presentacionIdLote: productToEdit.presentaciones?.find(p => p.esPrincipal)?.id || productToEdit.presentaciones?.[0]?.id || '',
        presentaciones: productToEdit.presentaciones?.length
          ? productToEdit.presentaciones.map(p => ({
              id: p.id, nombre: p.nombre, factor: p.factor, precio: p.precio,
              precioCompra: p.precioCompra ?? undefined, esPrincipal: p.esPrincipal,
            }))
          : [{ nombre: 'Unidad', factor: undefined, precio: undefined, precioCompra: undefined, esPrincipal: true }],
      });
      if (productToEdit.imagenes?.length) {
        setExistingImages(productToEdit.imagenes.map(img => ({ id: img.id, url: img.url })));
      } else {
        setExistingImages([]);
      }
      // Cargar lotes del producto
      setLotesLoading(true);
      ProductsService.getLotesByProduct(productToEdit.id).then(res => {
        if (res.success) setLotes(res.data || []);
        setLotesLoading(false);
      });
    } else {
      // Modo creación: valores por defecto limpios
      reset({
        nombre: '',
        descripcion: '',
        categoria: '',
        receta: false,
        codigoBarras: '',
        variante: '',
        registroInvima: '',
        laboratorio: '',
        principioActivo: '',
        ubicacion: '',
        stockBase: undefined,
        numeroLote: '',
        fechaVencimiento: '',
        proveedorId: '',
        numeroFactura: '',
        presentacionIdLote: '',
        presentaciones: [{ nombre: 'Unidad', factor: undefined, precio: undefined, precioCompra: undefined, esPrincipal: true }],
      });
      setSelectedImages([]);
      setImagePreviews([]);
      setExistingImages([]);
      setLotes([]);
      setLotesLoading(false);
      setSavingLoteId(null);
      setInvimaQuery('');
      setInvimaResults([]);
      setShowInvimaResults(false);
    }
  }, [isOpen, productToEdit, reset]);

  const handleClose = () => { setIsVisible(false); setTimeout(() => onClose(), 300); };
  const confirmClose = () => {
    setShowCloseConfirmModal(false);
    handleClose();
  };
  const handleAttemptClose = () => {
    const hayDatosSinGuardar = isDirty || selectedImages.length > 0;
    if (hayDatosSinGuardar && !isEditMode) {
      setShowCloseConfirmModal(true);
    } else {
      handleClose();
    }
  };
  const handleMinimize = () => { if (onMinimize) onMinimize(); };

  const onSubmit = async (data: any): Promise<void> => {
    setIsLoading(true); setErrorMessage(null);
    try {
      const shouldCreateLote = !isEditMode && !!data.numeroLote?.trim();
      const productData: Partial<Product> & { imagenesFiles?: File[] } = {
        nombre: data.nombre,
        descripcion: data.descripcion,
        categoria: data.categoria,
        requiereReceta: data.receta,
        codigoBarras: data.codigoBarras || undefined,
        variante: data.variante,
        registroInvima: data.registroInvima || undefined,
        laboratorio: data.laboratorio || undefined,
        principioActivo: data.principioActivo || undefined,
        ubicacion: data.ubicacion || undefined,
        stockBase: hasPermission('Productos', isEditMode ? 'editar stock' : 'crear stock') ? (shouldCreateLote ? 0 : data.stockBase) : undefined,
        presentaciones: data.presentaciones.map((p: any) => ({
          ...(p.id && { id: p.id }),
          nombre: p.nombre,
          factor: Number.isFinite(p.factor) && p.factor > 0 ? p.factor : 1,
          precio: hasPermission('Productos', isEditMode ? 'editar stock' : 'crear stock') ? (Number.isFinite(p.precio) && p.precio >= 0 ? p.precio : 0) : undefined,
          precioCompra: hasPermission('Productos', isEditMode ? 'editar stock' : 'crear stock') ? (Number.isFinite(p.precioCompra) && p.precioCompra >= 0 ? p.precioCompra : undefined) : undefined,
          esPrincipal: p.esPrincipal,
        })) as any,
        imagenesFiles: selectedImages.length > 0 ? selectedImages : undefined,
        imagenes: existingImages.length > 0 ? existingImages.map((img, idx) => ({ url: img.url, orden: idx })) : undefined as any,
      };
      if (isEditMode && productToEdit) {
        await ProductsService.update(productToEdit.id, productData);
        setNotificationConfig({ title: 'Producto actualizado', message: `${data.nombre} actualizado.`, type: 'success' });
      } else {
        const response = await ProductsService.create(productData);
        if (response.success && response.data && shouldCreateLote) {
          const createdProduct = response.data;
          const presentacionLote = createdProduct.presentaciones?.find((p: any) => p.id === data.presentacionIdLote) || createdProduct.presentaciones?.find((p: any) => p.esPrincipal) || createdProduct.presentaciones?.[0];
          if (presentacionLote) {
            const proveedorNombre = data.proveedorId
              ? suppliers.find(s => s.id === data.proveedorId)?.nombre
              : undefined;
            await ProductsService.addLote(createdProduct.id, {
              presentacionId: presentacionLote.id,
              numeroLote: data.numeroLote.trim(),
              cantidad: Math.floor((data.stockBase || 0) / (presentacionLote.factor || 1)),
              fechaVencimiento: data.fechaVencimiento || undefined,
              proveedor: proveedorNombre,
              proveedorId: data.proveedorId || undefined,
              numeroFactura: data.numeroFactura?.trim() || undefined,
            });
          }
        }
        setNotificationConfig({ title: 'Producto creado', message: `${data.nombre} creado.`, type: 'success' });
      }
      setShowNotification(true);
      setTimeout(() => { handleClose(); onSuccess?.(); }, 1500);
    } catch (error: any) {
      setErrorMessage(error.message || 'Error al guardar');
      setNotificationConfig({ title: 'Error', message: error.message || 'No se pudo guardar', type: 'error' });
      setShowNotification(true);
    } finally { setIsLoading(false); }
  };

  const ubicaciones = [
    ...Array.from({ length: 4 }, (_, i) => `Vitrina ${i + 1}`),
    ...Array.from({ length: 20 }, (_, i) => `Estantería ${i + 1}`),
    'Farmalay Inferior',
    'Farmalay Superior',
  ];

  const categorias = [
    { id: 'medicamentos', nombre: 'Medicamentos' },
    { id: 'cuidado-personal', nombre: 'Cuidado Personal' },
    { id: 'higiene-femenina', nombre: 'Higiene Femenina' },
    { id: 'salud-sexual', nombre: 'Salud Sexual' },
    { id: 'vitaminas-y-suplementos', nombre: 'Vitaminas y Suplementos' },
    { id: 'bebes', nombre: 'Bebés' },
    { id: 'equipos-medicos', nombre: 'Equipos Médicos' },
  ];

  const formatPrice = (price: number) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price);

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-[60]">
        <button
          onClick={() => onMaximize?.()}
          className="flex items-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-full shadow-lg hover:bg-emerald-700 transition-all animate-bounce"
        >
          <Package className="w-5 h-5" />
          <span className="text-sm font-medium">
            {isEditMode ? 'Continuar editando producto' : 'Continuar creando producto'}
          </span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <>
      <div className={`fixed inset-0 z-40 transition-opacity duration-300 lg:hidden ${isVisible ? 'opacity-100' : 'opacity-0'}`} onClick={handleAttemptClose} />
      <div className={`fixed right-0 top-20 h-[calc(100vh-5rem)] w-full max-w-7xl bg-gray-50 shadow-2xl z-40 transform transition-transform duration-300 ease-in-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{isEditMode ? 'Editar Producto' : 'Nuevo Producto'}</h2>
                <p className="text-sm text-gray-500">{isEditMode ? 'Modifica los datos del producto' : 'Completa la información para crear el producto'}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={handleMinimize} title="Minimizar" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                </svg>
              </button>
              <button onClick={handleAttemptClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form id="product-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">

              {/* Error */}
              {errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}

              {/* INVIMA Search */}
              {!isEditMode && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Search className="w-4 h-4 text-emerald-500" />
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Buscar en base INVIMA</h3>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      ref={invimaInputRef}
                      type="text"
                      value={invimaQuery}
                      onChange={(e) => {
                        setInvimaQuery(e.target.value);
                        if (e.target.value.length >= 3) {
                          setInvimaLoading(true);
                          setShowInvimaResults(true);
                          ProductsService.searchInvima(e.target.value, 10).then((res) => {
                            if (res.success) setInvimaResults(res.data || []);
                            setInvimaLoading(false);
                          });
                        } else {
                          setInvimaResults([]);
                          setShowInvimaResults(false);
                        }
                      }}
                      placeholder="Escribe el nombre del medicamento..."
                      className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-300 focus:border-gray-500 bg-white text-sm transition-colors"
                    />
                    {invimaLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
                  </div>
                  {showInvimaResults && invimaResults.length > 0 && (
                    <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {invimaResults.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => {
                            setValue('nombre', item.producto);
                            setValue('registroInvima', item.registroSanitario || '');
                            setValue('laboratorio', item.titular || '');
                            setValue('principioActivo', item.principioActivo || '');
                            setInvimaQuery(item.producto);
                            setShowInvimaResults(false);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-emerald-50 border-b border-gray-100 last:border-0 transition-colors"
                        >
                          <p className="text-sm font-semibold text-gray-900">{item.producto}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {item.principioActivo ? item.principioActivo.slice(0, 60) + (item.principioActivo.length > 60 ? '...' : '') : 'Sin principio activo'}
                            {' · '}
                            <span className="text-emerald-600 font-medium">{item.registroSanitario}</span>
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                  {showInvimaResults && invimaQuery.length >= 3 && !invimaLoading && invimaResults.length === 0 && (
                    <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 text-sm text-gray-500">
                      No se encontraron resultados en INVIMA
                    </div>
                  )}
                </div>
              )}

              {/* Grid 2 cols: Info Principal + Identificación */}
              <div className="grid grid-cols-2 gap-6">
                {/* Info Principal */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Package className="w-4 h-4 text-emerald-500" />
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Información Principal</h3>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nombre <span className="text-red-500">*</span></label>
                    <input {...register('nombre')} type="text" placeholder="Ej: Sevedol" className={`w-full px-4 py-2.5 rounded-lg border focus:border-gray-500 text-sm transition-colors bg-white ${errors.nombre ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} />
                    {errors.nombre && <p className="mt-1 text-xs text-red-500">{(errors.nombre as any)?.message}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Descripción</label>
                    <textarea {...register('descripcion')} rows={3} placeholder="Descripción del producto..." className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-gray-500 bg-white text-sm resize-none" />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Categoría <span className="text-red-500">*</span></label>
                    <CustomSelect
                      options={categorias.map(c => ({ value: c.id, label: c.nombre }))}
                      value={watch('categoria') || ''}
                      onChange={(v) => setValue('categoria', v as any)}
                      placeholder="Seleccionar categoría"
                      className={errors.categoria ? 'border-red-500' : ''}
                    />
                    {errors.categoria && <p className="mt-1 text-xs text-red-500">{(errors.categoria as any)?.message}</p>}
                  </div>

                  <div className="inline-flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Controller
                      name="receta"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <input
                          type="checkbox"
                          id="receta"
                          checked={!!value}
                          onChange={(e) => onChange(e.target.checked)}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                      )}
                    />
                    <label htmlFor="receta" className="cursor-pointer">
                      <span className="text-sm font-semibold text-gray-900">Requiere receta médica</span>
                      <p className="text-xs text-gray-500">Marcar si necesita prescripción médica para su venta</p>
                    </label>
                  </div>
                </div>

                {/* Identificación */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Tag className="w-4 h-4 text-emerald-500" />
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Identificación y Detalles</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Código de barras</label>
                      <input {...register('codigoBarras')} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} type="text" placeholder="770..." className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-gray-500 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Variante</label>
                      <input {...register('variante')} type="text" placeholder="Ej: Uva, Naranja" className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-gray-500 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Registro INVIMA</label>
                      <input {...register('registroInvima')} type="text" placeholder="2023M..." className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-gray-500 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Laboratorio</label>
                      <input {...register('laboratorio')} type="text" placeholder="Ej: Genfar" className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-gray-500 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Principio activo</label>
                      <input {...register('principioActivo')} type="text" placeholder="Ej: Acetaminofén" className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-gray-500 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Ubicación</label>
                      <CustomSelect
                        options={ubicaciones.map(u => ({ value: u, label: u }))}
                        value={watch('ubicacion') || ''}
                        onChange={(v) => setValue('ubicacion', v)}
                        placeholder="Seleccionar ubicación"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Stock y Lotes */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Boxes className="w-4 h-4 text-emerald-500" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Stock</h3>
                </div>

                <div className="grid grid-cols-[260px_1fr] gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Stock total (unidades base)
                      {isEditMode && <span className="ml-1 text-gray-400 font-normal">(solo lectura)</span>}
                      {!isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      {...register('stockBase')}
                      type="number"
                      min={0}
                      placeholder="Ej: 30 = 1 caja de 30 sobres"
                      disabled={isEditMode || !hasPermission('Productos', isEditMode ? 'editar stock' : 'crear stock')}
                      className={`w-full px-4 py-2.5 rounded-lg border focus:border-gray-500 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${errors.stockBase ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                    />
                    {errors.stockBase && <p className="mt-1 text-xs text-red-500">{(errors.stockBase as any)?.message}</p>}
                    <p className="text-xs text-gray-500 mt-1.5">
                      {isEditMode
                        ? 'El stock se gestiona automáticamente a través de los lotes. Edita un lote para cambiar el stock.'
                        : 'Conteo total de la unidad más pequeña (ej: sobres, tabletas, cápsulas)'}
                    </p>
                    {isEditMode && lotes.length > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Total en {lotes.length} lote{lotes.length !== 1 ? 's' : ''} registrado{lotes.length !== 1 ? 's' : ''}: <span className="font-bold">{lotes.reduce((sum, l) => sum + (l.cantidad || 0), 0)}</span> unidades base
                      </p>
                    )}
                  </div>

                  {/* Lote inicial - solo creación, solo super admin */}
                  {!isEditMode && hasPermission('Productos', 'crear stock') && (
                    <div className="p-4 bg-amber-50/70 rounded-xl border border-amber-200 space-y-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-bold text-amber-800">Lote inicial (opcional)</span>
                      </div>
                      <p className="text-xs text-amber-600">
                        Si ingresas número de lote, se registrará automáticamente con el stock total al crear el producto.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">Número de lote</label>
                          <input {...register('numeroLote')} type="text" placeholder="LOTE-2025-001" className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:border-gray-500 bg-white" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">Fecha de vencimiento</label>
                          <input {...register('fechaVencimiento')} type="date" className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:border-gray-500 bg-white" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">Proveedor</label>
                          <CustomSelect
                            options={suppliers.map((s) => ({ value: s.id, label: s.nombre }))}
                            value={watch('proveedorId') || ''}
                            onChange={(v) => setValue('proveedorId', v)}
                            placeholder="Seleccionar..."
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">Número de factura</label>
                          <input {...register('numeroFactura')} type="text" placeholder="FAC-2025-001234" className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:border-gray-500 bg-white" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">Presentación del lote</label>
                          <select {...register('presentacionIdLote')} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:border-gray-500 bg-white">
                            {watch('presentaciones')?.filter((p: any) => p.nombre && p.factor > 0).map((p: any, idx: number) => (
                              <option key={p.id || idx} value={p.id || ''}>{p.nombre} (factor {p.factor}){p.esPrincipal ? ' — Principal' : ''}</option>
                            ))}
                          </select>
                          <p className="text-[10px] text-amber-600 mt-1">El stock se dividirá por este factor para calcular la cantidad del lote.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Lotes existentes - solo edición */}
                  {isEditMode && productToEdit && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-700">Lotes registrados</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                            {lotes.length} lote{lotes.length !== 1 ? 's' : ''}
                          </span>
                          {hasPermission('Productos', 'crear stock') && (
                            <button
                              type="button"
                              onClick={() => setIsAddLoteOpen(true)}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                            >
                              <Plus className="w-3.5 h-3.5" /> Crear lote
                            </button>
                          )}
                        </div>
                      </div>
                      {lotesLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                        </div>
                      ) : lotes.length === 0 ? (
                        <div className="p-6 bg-blue-50/50 rounded-xl border border-blue-100 text-center">
                          <Package className="w-8 h-8 text-blue-300 mx-auto mb-2" />
                          <p className="text-sm text-blue-600 font-medium">Este producto no tiene lotes registrados</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {lotes.map((lote) => {
                            const presentacion = productToEdit.presentaciones?.find(p => p.id === lote.presentacionId);
                            const diasRestantes = lote.fechaVencimiento
                              ? Math.ceil((new Date(lote.fechaVencimiento).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                              : null;
                            return (
                              <div key={lote.id} className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 space-y-2">
                                {(() => {
                                  const presLote = productToEdit.presentaciones?.find(p => p.id === lote.presentacionId);
                                  const factorLote = presLote?.factor ?? 1;
                                  const cantidadPres = Math.floor((lote.cantidad || 0) / factorLote);
                                  return (
                                    <>
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Lote</label>
                                    <input type="text" value={lote.numeroLote} onChange={(e) => { setLotes(prev => prev.map(l => l.id === lote.id ? { ...l, numeroLote: e.target.value } : l)); }} className="w-full px-2 py-1.5 rounded-lg border border-blue-200 bg-white text-sm focus:ring-1 focus:ring-blue-500" />
                                  </div>
                                  <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Presentación</label>
                                    <select
                                      value={lote.presentacionId || ''}
                                      onChange={(e) => { setLotes(prev => prev.map(l => l.id === lote.id ? { ...l, presentacionId: e.target.value } : l)); }}
                                      className="w-full px-2 py-1.5 rounded-lg border border-blue-200 bg-white text-sm focus:ring-1 focus:ring-blue-500"
                                    >
                                      {productToEdit.presentaciones?.filter(p => p.activo).map((p) => (
                                        <option key={p.id} value={p.id}>{p.nombre} (factor {p.factor})</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Cantidad ({presLote?.nombre || 'unidades'})</label>
                                    <input type="number" min={0} value={cantidadPres} disabled={!hasPermission('Productos', 'editar stock')} onChange={(e) => { const val = parseInt(e.target.value) || 0; setLotes(prev => prev.map(l => l.id === lote.id ? { ...l, cantidad: val * factorLote } : l)); }} className="w-full px-2 py-1.5 rounded-lg border border-blue-200 bg-white text-sm text-right focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed" />
                                    <p className="text-[10px] text-gray-400 mt-0.5 text-right">= {cantidadPres * factorLote} unidades base</p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Vencimiento</label>
                                    <div className="flex items-center gap-1.5">
                                      <input type="date" value={lote.fechaVencimiento ? new Date(lote.fechaVencimiento).toISOString().split('T')[0] : ''} onChange={(e) => { setLotes(prev => prev.map(l => l.id === lote.id ? { ...l, fechaVencimiento: e.target.value } : l)); }} className="flex-1 px-2 py-1.5 rounded-lg border border-blue-200 bg-white text-sm focus:ring-1 focus:ring-blue-500" />
                                      {lote.fechaVencimiento && diasRestantes !== null && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold whitespace-nowrap ${diasRestantes <= 30 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                          {diasRestantes <= 0 ? 'Vencido' : diasRestantes <= 30 ? `${diasRestantes}d` : 'OK'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Proveedor</label>
                                    <CustomSelect
                                      options={suppliers.map((s) => ({ value: s.id, label: s.nombre }))}
                                      value={lote.proveedorId || ''}
                                      onChange={(v) => { setLotes(prev => prev.map(l => l.id === lote.id ? { ...l, proveedorId: v || undefined } : l)); }}
                                      placeholder="Seleccionar..."
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Número de factura</label>
                                    <input
                                      type="text"
                                      value={lote.numeroFactura || ''}
                                      onChange={(e) => { setLotes(prev => prev.map(l => l.id === lote.id ? { ...l, numeroFactura: e.target.value || undefined } : l)); }}
                                      placeholder="FAC-001"
                                      className="w-full px-2 py-1.5 rounded-lg border border-blue-200 bg-white text-sm focus:ring-1 focus:ring-blue-500"
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center justify-end gap-2 pt-1">
                                  <button type="button" onClick={async () => { setSavingLoteId(lote.id); const proveedorNombre = lote.proveedorId ? suppliers.find(s => s.id === lote.proveedorId)?.nombre : undefined; const res = await ProductsService.updateLote(lote.id, { numeroLote: lote.numeroLote, cantidad: Math.floor((lote.cantidad || 0) / factorLote), fechaVencimiento: lote.fechaVencimiento, proveedor: proveedorNombre, proveedorId: lote.proveedorId, numeroFactura: lote.numeroFactura, presentacionId: lote.presentacionId }); if (res.success) { toast.success('Lote actualizado'); } else { toast.error(res.message || 'Error'); } setSavingLoteId(null); }} disabled={savingLoteId === lote.id} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs rounded-lg font-medium transition-colors">
                                    {savingLoteId === lote.id ? 'Guardando...' : 'Guardar'}
                                  </button>
                                  <button type="button" onClick={async () => { if (!confirm('¿Eliminar este lote? Esta acción afecta el stock del producto.')) return; setSavingLoteId(lote.id); const res = await ProductsService.deleteLote(lote.id); if (res.success) { toast.success('Lote eliminado'); setLotes(prev => prev.filter(l => l.id !== lote.id)); } else { toast.error(res.message || 'Error'); } setSavingLoteId(null); }} disabled={savingLoteId === lote.id} className="px-3 py-1.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-xs rounded-lg font-medium transition-colors flex items-center gap-1">
                                    <Trash2 className="w-3 h-3" /> Eliminar
                                  </button>
                                </div>
                                    </>
                                  );
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Presentaciones */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-emerald-500" />
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Presentaciones y Precios</h3>
                  </div>
                  <button type="button" onClick={() => append({ nombre: '', factor: undefined, precio: undefined, precioCompra: undefined, esPrincipal: false })} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium transition-colors">
                    <Plus className="w-3.5 h-3.5" /> Agregar presentación
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  La presentación con <strong>factor 1</strong> es la unidad más pequeña (ej: 1 sobre). 
                  Una <strong>caja de 30</strong> usa factor 30. El stock se calcula dividiendo el stock base entre el factor.
                </p>
                {errors.presentaciones && <p className="text-sm text-red-500 mb-3">{(errors.presentaciones as any)?.message}</p>}
                <div className="space-y-4">
                  {fields.map((field, index) => {
                    const precio = watch(`presentaciones.${index}.precio`) || 0;
                    const precioCompra = watch(`presentaciones.${index}.precioCompra`) || 0;
                    const factor = watch(`presentaciones.${index}.factor`) || 1;
                    const stockBase = watch('stockBase') || 0;
                    const stockCalculado = Math.floor(stockBase / factor);
                    const margen = precioCompra > 0 ? ((precio - precioCompra) / precioCompra) * 100 : 0;
                    const gananciaUnidad = precio - precioCompra;
                    return (
                      <div key={field.id} className={`p-5 rounded-xl border-2 space-y-4 ${watch(`presentaciones.${index}.esPrincipal`) ? 'border-emerald-300 bg-emerald-50/30' : 'border-gray-200 bg-gray-50/50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Pill className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-bold text-gray-900">Presentación {index + 1}</span>
                            {watch(`presentaciones.${index}.esPrincipal`) && (
                              <span className="text-[10px] font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">Principal</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                              <input type="checkbox" {...register(`presentaciones.${index}.esPrincipal`)} className="rounded text-emerald-600 focus:ring-emerald-500" /> Principal
                            </label>
                            {fields.length > 1 && (
                              <button type="button" onClick={() => remove(index)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Nombre</label>
                            <CustomSelect
                              options={[
                                { value: 'Caja', label: 'Caja' },
                                { value: 'Blister', label: 'Blister' },
                                { value: 'Unidad', label: 'Unidad' },
                              ]}
                              value={watch(`presentaciones.${index}.nombre`) || ''}
                              onChange={(v) => setValue(`presentaciones.${index}.nombre`, v, { shouldValidate: true })}
                              placeholder="Seleccionar"
                              className="text-sm"
                            />
                            {(errors.presentaciones as any)?.[index]?.nombre && <p className="text-[10px] text-red-500 mt-0.5">{(errors.presentaciones as any)[index]?.nombre?.message}</p>}
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Factor <span className="text-red-500">*</span></label>
                            <input {...register(`presentaciones.${index}.factor`)} type="number" min={1} placeholder="Cuántas unidades" className={`w-full px-3 py-2 rounded-lg border text-sm focus:border-gray-500 bg-white ${(errors.presentaciones as any)?.[index]?.factor ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} />
                            <p className="text-[10px] text-gray-400 mt-0.5">Ej: Caja de 30 → factor 30</p>
                            {(errors.presentaciones as any)?.[index]?.factor && <p className="text-[10px] text-red-500 mt-0.5">{(errors.presentaciones as any)[index]?.factor?.message}</p>}
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Costo</label>
                            <input {...register(`presentaciones.${index}.precioCompra`)} type="number" min={0} placeholder="0" disabled={!hasPermission('Productos', isEditMode ? 'editar stock' : 'crear stock')} className={`w-full px-3 py-2 rounded-lg border text-sm focus:border-gray-500 bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${(errors.presentaciones as any)?.[index]?.precioCompra ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} />
                            {(errors.presentaciones as any)?.[index]?.precioCompra && <p className="text-[10px] text-red-500 mt-0.5">{(errors.presentaciones as any)[index]?.precioCompra?.message}</p>}
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Precio venta <span className="text-red-500">*</span></label>
                            <input {...register(`presentaciones.${index}.precio`)} type="number" min={0} placeholder="0" disabled={!hasPermission('Productos', isEditMode ? 'editar stock' : 'crear stock')} className={`w-full px-3 py-2 rounded-lg border text-sm focus:border-gray-500 bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${(errors.presentaciones as any)?.[index]?.precio ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} />
                            {(errors.presentaciones as any)?.[index]?.precio && <p className="text-[10px] text-red-500 mt-0.5">{(errors.presentaciones as any)[index]?.precio?.message}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold">
                            <Boxes className="w-3.5 h-3.5" /> Stock: {stockCalculado} unidades
                          </span>
                          <span className="text-xs text-gray-500">(factor {factor})</span>
                          {precioCompra > 0 && precio > 0 && (
                            <>
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${margen >= 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                Margen: {margen.toFixed(1)}%
                              </span>
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${gananciaUnidad >= 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                Ganancia: {formatPrice(gananciaUnidad)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Imágenes */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <ImageIcon className="w-4 h-4 text-emerald-500" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Imágenes del Producto</h3>
                </div>

                {existingImages.length > 0 && (
                  <div className="grid grid-cols-6 gap-3 mb-4">
                    {existingImages.map((img) => (
                      <div key={img.id} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
                        <img src={getProductImageUrl(img.url)} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setExistingImages(prev => prev.filter(i => i.id !== img.id))} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {imagePreviews.length > 0 && (
                  <div className="grid grid-cols-6 gap-3 mb-4">
                    {imagePreviews.map((url, idx) => (
                      <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => { setSelectedImages(prev => prev.filter((_, i) => i !== idx)); setImagePreviews(prev => { URL.revokeObjectURL(prev[idx]); return prev.filter((_, i) => i !== idx); }); }} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <label className="flex items-center justify-center w-full px-4 py-8 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors">
                  <div className="flex flex-col items-center gap-2 text-gray-500">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                    <span className="text-sm font-medium">Haz clic para subir imágenes</span>
                    <span className="text-xs text-gray-400">JPG, PNG, WebP • Máx 5 MB • Hasta 5 imágenes</span>
                  </div>
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => { const files = Array.from(e.target.files || []); if (files.length === 0) return; const total = selectedImages.length + existingImages.length + files.length; if (total > 5) { setErrorMessage('Máximo 5 imágenes por producto'); return; } const valid = files.filter(f => f.size <= 5 * 1024 * 1024); if (valid.length < files.length) { setErrorMessage('Algunas imágenes exceden 5 MB y fueron omitidas'); } setSelectedImages(prev => [...prev, ...valid]); setImagePreviews(prev => [...prev, ...valid.map(file => URL.createObjectURL(file))]); }} />
                </label>
                {(selectedImages.length + existingImages.length) > 0 && (
                  <p className="mt-3 text-xs text-gray-500 font-medium">
                    {selectedImages.length + existingImages.length} / 5 imágenes
                  </p>
                )}
              </div>

            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex gap-3">
              <button type="button" onClick={handleAttemptClose} className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium">Cancelar</button>
              <button type="submit" form="product-form" disabled={isLoading} className="flex-1 px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-colors">
                {isLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Guardando...</> : <><CheckCircle className="w-5 h-5" /> {isEditMode ? 'Actualizar Producto' : 'Crear Producto'}</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showNotification && notificationConfig && (
        <NotificationToast isOpen={showNotification} title={notificationConfig.title} message={notificationConfig.message} type={notificationConfig.type} onClose={() => setShowNotification(false)} />
      )}

      {isEditMode && productToEdit && (
        <AddLoteDrawer isOpen={isAddLoteOpen} onClose={() => setIsAddLoteOpen(false)} onSuccess={() => { setLotesLoading(true); ProductsService.getLotesByProduct(productToEdit.id).then(res => { if (res.success) setLotes(res.data || []); setLotesLoading(false); }); }} product={productToEdit} />
      )}

      {showCloseConfirmModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl shadow-2xl p-6 bg-white text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">¿Aún no has creado el producto?</h3>
            <p className="text-sm text-gray-500 mb-6">Tienes cambios sin guardar. ¿Qué deseas hacer?</p>
            <div className="flex flex-col gap-2">
              <button onClick={() => { setShowCloseConfirmModal(false); handleMinimize(); }} className="w-full py-2.5 rounded-lg border font-medium text-sm border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">Seguir editando después</button>
              <button onClick={confirmClose} className="w-full py-2.5 rounded-lg font-medium text-sm text-red-600 hover:bg-red-50 transition-colors">Descartar y cerrar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateProductDrawer;
